/* eslint-disable no-debugger */
import { Fragment } from "react";
import StandardKendoReport from "@usas/ui-web/components/embeddedReports/kendoReports/StandardKendoReport";
import PopUpModal from "@usas/ui-web/components/dialogs/PopUpModal";
import useCompetitionDetailKendoReport from "./UseCompetitionDetailKendoReport";
import PrimaryButton from "@usas/ui-web/components/buttons/PrimaryButton";
import CompetitionCategoryMultiSelect from "@usas/ui-web/components/multiSelect/competitionCategoryMultiSelect/CompetitionCategoryMultiSelect";
import ZoneMultiSelect from "@usas/ui-web/components/multiSelect/zoneMultiSelect/ZoneMultiSelect";
import LscMultiSelect from "@usas/ui-web/components/multiSelect/lscMultiSelect/LscMultiSelect";
import ClubMultiSelect from "@usas/ui-web/components/multiSelect/clubMultiSelect/ClubMultiSelect";
import OrgRoleMultiSelect from "@usas/ui-web/components/multiSelect/orgRoleMultiSelect/OrgRoleMultiSelect";

import Dropdown from "@usas/ui-web/components/dropdowns/Dropdown";
import Input from "@usas/ui-web/components/inputs/Input";
import Headings from "@usas/ui-web/components/headings/Headings";
import QuestionIcon from "@usas/ui-web/components/icons/QuestionIcon";
import SecondaryButton from "@usas/ui-web/components/buttons/SecondaryButton";
import OrgUnitDropdown from "../../components/dropdowns/orgUnitDropdown/OrgUnitDropdown";
import YesNoSwitch from "@usas/ui-web/components/yesNoSwitch/YesNoSwitch";
import style from "./CompetitionDetailKendoReport.module.css";

// NOTE: if you have are using basic single select parameters like DDLs and textboxes you can dynamically gerenerate the parametes from json.
// see ClubTimesNew.jsx. If you want to use a specic complex component like EventMultiSelect you can create a form as in this example.

const CompetitionDetailKendoReport = () => {
  const {
    formState,
    errorState,
    updateFormState,
    reportParameters,
    parameters,
    columns,
    modalState,
    orgUnitState,
    onModalCancelClicked,
    handleSubmit,
    onFormValueChanged,
    onValueTextPairChanged,
    onRunReportClicked,
    onClearForm,
    seasonYearState,
    excelColumns,
    clubFilterIds,
    REPORT_INFO_ID
  } = useCompetitionDetailKendoReport();

  return (
    <Fragment>
      <form onSubmit={handleSubmit} noValidate>
        <div className="row">
          <div className="col-xs-12">
            <Headings.H3>Competition Detail Report</Headings.H3>
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12 col-sm-6 col-md-3">
            <OrgUnitDropdown 
              label="Organizations*"
              name="orgUnitId"
              value={formState.orgUnitId}
              error={errorState.orgUnitId}  
              message={errorState.orgUnitId}
              reportInfoId={REPORT_INFO_ID}
              onChange={(newValue, newValueLabel, e) => { 
                onValueTextPairChanged(newValue, 'orgUnitId', newValueLabel, 'orgUnitName', e); 
                onFormValueChanged('zones', []);
                onFormValueChanged('lscs', []);
                onFormValueChanged('clubs', []);
              }}
            />
          </div>
        </div> 
         <div className="row">
          {(orgUnitState.arrayData.find(ou => ou.orgUnitId === formState.orgUnitId)?.orgLevelId === 12 || formState.orgUnitId === '') &&
          <>
          <div className="col-xs-12 col-sm-6 col-md-3">
            <ZoneMultiSelect
              label={<span>Zones&nbsp;&nbsp;<QuestionIcon toolTipText="Leave blank to include all zones." /></span>}
              name="zones"
              value={formState.zones}
              error={errorState.zones}
              message={errorState.zones}
              onChange={(value) => {
                onFormValueChanged('zones', value);
                onFormValueChanged('lscs', []);
                onFormValueChanged('clubs', []);
              }} />
          </div>
          <div className="col-xs-12 col-sm-6 col-md-3">
            <LscMultiSelect
              label={<span>LSCs&nbsp;&nbsp;<QuestionIcon toolTipText="Leave blank to include all LSCs." /></span>}
              name="lscs"
              value={formState.lscs}
              error={errorState.lscs}
              message={errorState.lscs}
              filterIds={formState.zones.map(c => c.id).join(',')}
              onChange={(value) => {
                onFormValueChanged('lscs', value);
                onFormValueChanged('clubs', []);
              }} />
          </div>
          </>
          }
          {orgUnitState.arrayData.find(ou => ou.orgUnitId === formState.orgUnitId)?.orgLevelId !== 3 &&
          <div className="col-xs-12 col-sm-6 col-md-3">
            <ClubMultiSelect
              label={<span>Clubs&nbsp;&nbsp;<QuestionIcon toolTipText="Leave blank to include all clubs." /></span>}
              name="clubs"
              value={formState.clubs}
              error={errorState.clubs}
              message={errorState.clubs}
              filterIds={
                clubFilterIds
              }
              onChange={(value) => { onFormValueChanged('clubs', value); }} />
          </div>
          }
          <div className="col-xs-12 col-sm-6 col-md-3">
            <CompetitionCategoryMultiSelect
              label={<span>Competition Categories&nbsp;&nbsp;<QuestionIcon toolTipText="Leave blank to include all competition categories." /></span>}
              name="competitionCategories"
              value={formState.genders}
              error={errorState.genders}
              message={errorState.genders}
              onChange={(value) => { onFormValueChanged('genders', value); }} 
              showMixed={false} />
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12 col-sm-6 col-md-3">
            <Dropdown
              label={<span>Season Year&nbsp;&nbsp;<QuestionIcon toolTipText="If you select a Season Year, do not enter a date range." /></span>}
              name="season"
              options={seasonYearState.options}
              value={formState.seasonId}
              error={errorState.seasonId}
              message={errorState.seasonId}
              isLoading={seasonYearState.isLoading}
              onChange={(newValue, newValueLabel, e) => {
                e && e.target && e.target.value &&
                  onValueTextPairChanged(newValue, 'seasonId', newValueLabel, 'seasonYear', e);
              }} />
          </div>
          <div className="col-xs-12 col-sm-6 col-md-3">
            <Input
              label="Min Age"
              name="minAge"
              type="number"
              min="1"
              max="99"
              value={formState.minAge}
              error={errorState.minAge}
              message={errorState.minAge}
              onChange={(value) => { onFormValueChanged('minAge', value); }} />
          </div>
          <div className="col-xs-12 col-sm-6 col-md-3">
            <Input
              label="Max Age"
              name="maxAge"
              type="number"
              min="1"
              max="99"
              value={formState.maxAge}
              error={errorState.maxAge}
              message={errorState.maxAge}
              onChange={(value) => { onFormValueChanged('maxAge', value); }} />
          </div>
          <div className="col-xs-12 col-sm-6 col-md-3">
            <OrgRoleMultiSelect
              label={<span>Membership Types&nbsp;&nbsp;<QuestionIcon toolTipText="Leave blank to include all membership types." /></span>}
              name="memberTypes"
              value={formState.memberTypes}
              error={errorState.memberTypes}
              message={errorState.memberTypes}
              onChange={(value) => { onFormValueChanged('memberTypes', value); }} />
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12 col-sm-6 col-md-3">
            <YesNoSwitch
              // label="Active Roster?"
              label={<span>Active Roster?&nbsp;&nbsp;<QuestionIcon toolTipText="Displays Current Roster Only for Current Year, and End of Season Roster for Previous Years." /></span>}
              name="activeRoster"
              checked={formState.activeRoster === true}
              error={errorState.activeRoster}
              message={errorState.activeRoster}
              value={formState.activeRoster}
              onChange={(value) => { updateFormState('activeRoster', value); }} />
          </div>
          <div className="col-xs-12 col-sm-6 col-md-3">
            <YesNoSwitch
              label="Export Detail Data to Excel"
              name="exportDetailData"
              checked={formState.exportDetailData === true}
              error={errorState.exportDetailData}
              message={errorState.exportDetailData}
              value={formState.exportDetailData}
              onChange={(value) => { updateFormState('exportDetailData', value); }} />
          </div>
        </div>
        <div className="row usas-extra-top-margin usas-extra-bottom-margin">
          <div className="col-xs-12 usas-extra-top-margin">
            <PrimaryButton type="button" onClick={(e) => onRunReportClicked(e)}>Run</PrimaryButton>&nbsp;
            <SecondaryButton type="button" onClick={() => onClearForm()}>Clear Form</SecondaryButton>
          </div>
        </div>
      </form>
      <PopUpModal
        className={style.reportModalTop}
        widthPct={98}
        maxWidth={2050}
        displayPopUp={modalState.displayPopUp}
        onModalCanceled={onModalCancelClicked}>
        <StandardKendoReport
          reportInfoId={REPORT_INFO_ID}
          reportParameters={reportParameters}
          title="Competition Detail Report"
          parameters={parameters}
          columns={columns}
          usePagination={false}
          pageSize={10}
          enablePdfExport={true}
          enableExcelExport={true}
          gridHeight="750px"
          detailArrayName="eventDetail"
          detailReportTitle="Event Detail"
          detailGridHeight="300px"
          excelColumns={excelColumns}
          exportDetailData={formState.exportDetailData}
        />
      </PopUpModal>
    </Fragment>
  );
}

export default CompetitionDetailKendoReport;